import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { WS_DOMAIN, WS_PROTOCOL } from '@/config/constantes'
import { useUserStore } from '@/config/store/userStore'
import { chatApi, resourceApi } from '@/services/api'
import type { ChatConversationListItem, ChatMessage } from '@/services/chat/chatHttp.service'
import { canUserAccessChat } from '@/config/router/navigationsGuards/guardChat'

export type ChatApplicationSettings = {
  floatingWidgetHidden?: boolean
  /** Email digests for unread messages (default: enabled) */
  unreadEmailEnabled?: boolean
  /** Absolute days from message creation, e.g. [1, 3, 7] */
  unreadEmailDays?: number[]
}

const CHAT_SETTINGS_KEY = 'chat'
const LEGACY_WIDGET_HIDDEN_KEY = 'bc-chat-widget-hidden'
export const DEFAULT_UNREAD_EMAIL_DAYS = [1, 3, 7]

export type ChatSocketEvent =
  | { event: 'connected'; payload: { resourceId: string; firmId: string } }
  | { event: 'subscribed'; payload: { conversationId: string } }
  | { event: 'pong'; payload: Record<string, unknown> }
  | { event: 'message.created'; payload: { conversationId: string; message: ChatMessage } }
  | { event: 'message.updated'; payload: { conversationId: string; message: ChatMessage } }
  | { event: 'message.deleted'; payload: { conversationId: string; message: ChatMessage } }
  | { event: 'conversation.updated'; payload: { conversationId: string } }
  | { event: 'receipt.updated'; payload: { conversationId: string; resourceId: string; lastReadAt: string; lastReadMessageId?: string | null } }
  | { event: 'reaction.updated'; payload: { conversationId: string; messageId: string; reactions: ChatMessage['reactions'] } }

type ChatEventHandler = (event: ChatSocketEvent) => void

export const useChatStore = defineStore('chat', () => {
  const userStore = useUserStore()

  const unreadCount = ref(0)
  const conversations = ref<ChatConversationListItem[]>([])
  const isLoadingConversations = ref(false)

  const readChatSettings = (): ChatApplicationSettings => {
    const settings = userStore.user?.applicationSettings
    const chatSettings = settings?.[CHAT_SETTINGS_KEY]
    if (chatSettings && typeof chatSettings === 'object' && !Array.isArray(chatSettings)) {
      return chatSettings as ChatApplicationSettings
    }
    return {}
  }

  const normalizeUnreadEmailDays = (raw?: number[]) => {
    const days = (Array.isArray(raw) ? raw : DEFAULT_UNREAD_EMAIL_DAYS)
      .map(value => Number(value))
      .filter(value => Number.isFinite(value) && value >= 1 && value <= 90)
      .map(value => Math.floor(value))
    const uniqueSorted = [...new Set(days)].sort((a, b) => a - b).slice(0, 5)
    return uniqueSorted.length ? uniqueSorted : [...DEFAULT_UNREAD_EMAIL_DAYS]
  }

  const readFloatingWidgetHidden = (): boolean => !!readChatSettings().floatingWidgetHidden

  // Floating widget UI state (persisted in resource applicationSettings)
  const widgetHidden = ref(readFloatingWidgetHidden())
  const widgetExpanded = ref(false)
  const widgetConversationId = ref<string | null>(null)
  /** Conversation currently open in full page or widget — suppresses unread bump */
  const activeConversationId = ref<string | null>(null)

  const unreadEmailEnabled = computed(() => readChatSettings().unreadEmailEnabled !== false)
  const unreadEmailDays = computed(() => normalizeUnreadEmailDays(readChatSettings().unreadEmailDays))

  const migrateLegacyWidgetPreference = () => {
    try {
      const legacy = localStorage.getItem(LEGACY_WIDGET_HIDDEN_KEY)
      if (legacy === null) return
      localStorage.removeItem(LEGACY_WIDGET_HIDDEN_KEY)
      if (legacy === 'true' && !widgetHidden.value) {
        void persistChatSettings({ floatingWidgetHidden: true })
      }
    }
    catch {
      // ignore
    }
  }

  const persistChatSettings = async (patch: Partial<ChatApplicationSettings>) => {
    const user = userStore.user
    if (!user?._id) return

    const currentSettings = { ...(user.applicationSettings || {}) }
    const currentChat = readChatSettings()
    const chatSettings: ChatApplicationSettings = {
      ...currentChat,
      ...patch,
    }
    if (patch.unreadEmailDays) {
      chatSettings.unreadEmailDays = normalizeUnreadEmailDays(patch.unreadEmailDays)
    }
    if (typeof patch.floatingWidgetHidden === 'boolean') {
      widgetHidden.value = patch.floatingWidgetHidden
    }

    const nextSettings = {
      ...currentSettings,
      [CHAT_SETTINGS_KEY]: chatSettings,
    }
    user.applicationSettings = nextSettings

    try {
      await resourceApi.updateResource(user._id, { applicationSettings: nextSettings }).promise
    }
    catch (err) {
      console.error('[chat] persistChatSettings', err)
      throw err
    }
  }

  const persistWidgetHidden = async (hidden: boolean) => {
    await persistChatSettings({ floatingWidgetHidden: hidden })
  }

  const socket = shallowRef<WebSocket | null>(null)
  const isConnected = ref(false)
  const handlers = new Set<ChatEventHandler>()
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let pingTimer: ReturnType<typeof setInterval> | null = null
  let pollTimer: ReturnType<typeof setInterval> | null = null

  const canAccess = computed(() => !!userStore.token && canUserAccessChat(userStore.user))

  const widgetConversation = computed(() =>
    conversations.value.find(c => c._id === widgetConversationId.value) || null,
  )

  const emit = (event: ChatSocketEvent) => {
    handlers.forEach(handler => handler(event))
  }

  const onEvent = (handler: ChatEventHandler) => {
    handlers.add(handler)
    return () => handlers.delete(handler)
  }

  const refreshUnread = async () => {
    if (!canAccess.value) {
      unreadCount.value = 0
      return
    }
    try {
      const response = await chatApi.getUnreadCount().promise
      unreadCount.value = response.unreadCount || 0
    }
    catch (err) {
      console.error('[chat] refreshUnread', err)
    }
  }

  const refreshConversations = async () => {
    if (!canAccess.value) return
    isLoadingConversations.value = true
    try {
      conversations.value = await chatApi.listConversations({ limit: 30 }).promise
    }
    catch (err) {
      console.error('[chat] refreshConversations', err)
    }
    finally {
      isLoadingConversations.value = false
    }
  }

  const subscribeConversation = (conversationId: string) => {
    if (socket.value?.readyState === WebSocket.OPEN) {
      socket.value.send(JSON.stringify({ type: 'subscribe', conversationId }))
    }
  }

  const unsubscribeConversation = (conversationId: string) => {
    if (socket.value?.readyState === WebSocket.OPEN) {
      socket.value.send(JSON.stringify({ type: 'unsubscribe', conversationId }))
    }
  }

  const handleIncomingEvent = (event: ChatSocketEvent) => {
    emit(event)

    if (event.event === 'message.created') {
      const { conversationId, message } = event.payload
      const myId = String(userStore.user?._id || '')
      const isMine = String(message.senderId) === myId
      const isActive = activeConversationId.value === conversationId
        || (widgetExpanded.value && widgetConversationId.value === conversationId)

      const index = conversations.value.findIndex(c => c._id === conversationId)
      if (index >= 0) {
        const current = conversations.value[index]
        const nextUnread = (isMine || isActive)
          ? 0
          : (current.unreadCount || 0) + 1
        conversations.value[index] = {
          ...current,
          lastMessageAt: message._created as Date,
          lastMessagePreview: message.deletedAt
            ? ''
            : String(message.body || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() || current.lastMessagePreview,
          lastMessageId: message._id,
          unreadCount: nextUnread,
        }
        const [item] = conversations.value.splice(index, 1)
        conversations.value.unshift(item)
      }
      else {
        refreshConversations()
      }

      if (!isMine && !isActive) {
        unreadCount.value += 1
      }
      else if (isActive) {
        refreshUnread()
      }
    }
    else if (event.event === 'conversation.updated') {
      refreshConversations()
      refreshUnread()
    }
    else if (event.event === 'receipt.updated') {
      if (String(event.payload.resourceId) === String(userStore.user?._id)) {
        const index = conversations.value.findIndex(c => c._id === event.payload.conversationId)
        if (index >= 0) {
          conversations.value[index] = { ...conversations.value[index], unreadCount: 0 }
        }
        refreshUnread()
      }
    }
  }

  const connect = () => {
    if (!canAccess.value) return
    const resourceId = userStore.user?._id
    const firmId = userStore.user?._firmId
    if (!resourceId || !firmId) return

    if (socket.value && (socket.value.readyState === WebSocket.OPEN || socket.value.readyState === WebSocket.CONNECTING)) {
      return
    }

    const url = `${WS_PROTOCOL}${WS_DOMAIN}/chat?resourceId=${resourceId}&firmId=${firmId}`
    const ws = new WebSocket(url)
    socket.value = ws

    ws.onopen = () => {
      isConnected.value = true
      if (pingTimer) clearInterval(pingTimer)
      pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }))
        }
      }, 25000)
      if (widgetConversationId.value) {
        subscribeConversation(widgetConversationId.value)
      }
    }

    ws.onmessage = (messageEvent) => {
      try {
        const data = JSON.parse(String(messageEvent.data)) as ChatSocketEvent
        handleIncomingEvent(data)
      }
      catch {
        // ignore
      }
    }

    ws.onclose = () => {
      isConnected.value = false
      if (pingTimer) {
        clearInterval(pingTimer)
        pingTimer = null
      }
      if (reconnectTimer) clearTimeout(reconnectTimer)
      reconnectTimer = setTimeout(connect, 3000)
    }

    ws.onerror = () => {
      ws.close()
    }
  }

  const disconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    if (pingTimer) {
      clearInterval(pingTimer)
      pingTimer = null
    }
    socket.value?.close()
    socket.value = null
    isConnected.value = false
  }

  const start = async () => {
    if (!canAccess.value) return
    widgetHidden.value = readFloatingWidgetHidden()
    migrateLegacyWidgetPreference()
    await Promise.all([refreshUnread(), refreshConversations()])
    connect()
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = setInterval(refreshUnread, 60000)
  }

  const stop = () => {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
    disconnect()
  }

  const openWidget = () => {
    if (widgetHidden.value) return
    widgetExpanded.value = true
    refreshConversations()
  }

  const openConversationInWidget = (conversationId: string) => {
    if (widgetHidden.value) return
    if (widgetConversationId.value && widgetConversationId.value !== conversationId) {
      unsubscribeConversation(widgetConversationId.value)
    }
    widgetConversationId.value = conversationId
    activeConversationId.value = conversationId
    widgetExpanded.value = true
    subscribeConversation(conversationId)

    const index = conversations.value.findIndex(c => c._id === conversationId)
    if (index >= 0) {
      conversations.value[index] = { ...conversations.value[index], unreadCount: 0 }
    }
    refreshUnread()
  }

  const backToConversationList = () => {
    if (widgetConversationId.value) {
      unsubscribeConversation(widgetConversationId.value)
    }
    widgetConversationId.value = null
    activeConversationId.value = null
  }

  const setActiveConversation = (conversationId: string | null) => {
    activeConversationId.value = conversationId
  }

  const collapseWidget = () => {
    widgetExpanded.value = false
    if (widgetConversationId.value) {
      unsubscribeConversation(widgetConversationId.value)
    }
    widgetConversationId.value = null
    activeConversationId.value = null
  }

  const hideWidget = () => {
    collapseWidget()
    void persistWidgetHidden(true)
  }

  const showWidget = () => {
    void persistWidgetHidden(false)
  }

  const markLocalRead = (conversationId: string) => {
    const index = conversations.value.findIndex(c => c._id === conversationId)
    if (index >= 0) {
      conversations.value[index] = { ...conversations.value[index], unreadCount: 0 }
    }
    refreshUnread()
  }

  const chatDotConfig = computed(() => {
    if (unreadCount.value === 0) return false as const
    return {
      content: unreadCount.value > 99 ? '99+' : String(unreadCount.value),
      bg: 'error' as const,
    }
  })

  return {
    unreadCount,
    conversations,
    isLoadingConversations,
    widgetHidden,
    widgetExpanded,
    widgetConversationId,
    widgetConversation,
    activeConversationId,
    unreadEmailEnabled,
    unreadEmailDays,
    isConnected,
    canAccess,
    chatDotConfig,
    onEvent,
    subscribeConversation,
    unsubscribeConversation,
    refreshUnread,
    refreshConversations,
    start,
    stop,
    openWidget,
    collapseWidget,
    hideWidget,
    showWidget,
    openConversationInWidget,
    backToConversationList,
    setActiveConversation,
    markLocalRead,
    persistChatSettings,
    normalizeUnreadEmailDays,
  }
})
